import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MemberProfileDto,
  TasksSummaryDto,
  PaginatedResponseDto,
  SearchMemberDto,
  PersonDto,
  AddressDto,
  PeopleDto,
  BranchDto, OrganizationDto, HoldDto, CollateralDto, SavingDto, LendingDto, MemberBankingDto, MemberSyncDto
} from '@core/services/dto';
import { MemberUrls, NoteUrls, ProspectUrls } from '@core/constants/endpoints.constants';
import { ApiClient } from '@core/services/api-client.http.service';
import { NoteDto } from '@core/services/dto/note.dto';
import { AddPersonToOrganizationApiRequestModel } from '@shared/models/requests/add-person-to-organization.request.dto';
import { MemberProfileRequestDto } from '@shared/models/requests/member-profile.request.dto';
import { LinkSearchRequestDto } from '@shared/models/requests/link-search.request.dto';
import { AddressModel, OrganizationModel, PeopleModel, PersonModel, ProvinceModel } from '@shared/models';
import { PeopleApiRequest } from '@shared/models/requests/people.request.dto';
import { PersonUpdateApiRequestModel } from '@shared/models/requests/person-update.request.dto';
import { BankIntegrationConfigurationDto, CoreBankingIntegrationListDto } from '@shared/models/bank-integration-configuration.dto';
import { OffersSummaryDto } from '../dto/offers-summary.dto';
import { AccessHistoryDto } from '../dto/accesshistory.dto';
import { AccessHistoryApiRequest } from '@shared/models/requests/accesshistory.request.dto';

@Injectable({
  providedIn: 'any',
})
export class MemberProfileApiService {
  constructor(private api: ApiClient) { }

  getMemberProfileInfo(memberId: number): Observable<MemberProfileDto> {
    return this.api.get<MemberProfileDto>(MemberUrls.member(memberId));
  }

  isTwilioConfigured(siteId: string): Observable<any> {
    return this.api.get<any>(MemberUrls.twillio(siteId));
  }

  getMembers(
    request: MemberProfileRequestDto
  ): Observable<PaginatedResponseDto<MemberProfileDto>> {
    return this.api.get<PaginatedResponseDto<MemberProfileDto>>(
      MemberUrls.members,
      request
    );
  }

  getAddressFields(
    countryCode: string,
    addressType: string
  ): Observable<any> {
    return this.api.get(MemberUrls.getAddressFields(countryCode, addressType));
  }

  // eslint-disable-next-line
  getCoreBanking(
    memberNumber: number,
    branchId: number,
    request: MemberProfileRequestDto
  ): Observable<PaginatedResponseDto<MemberBankingDto>> {
    let url = MemberUrls.coreBanking(memberNumber);

    if (!!branchId) {
      url += `&branchId=${branchId}`;
    }

    return this.api.get<PaginatedResponseDto<MemberBankingDto>>(url, request);
  }

  searchMember(request?: LinkSearchRequestDto): Observable<SearchMemberDto> {
    return this.api.get<SearchMemberDto>(MemberUrls.members, request);
  }

  getSummary(
    memberId: number,
    statuses: string[]
  ): Observable<TasksSummaryDto> {
    return this.api.get<TasksSummaryDto>(
      MemberUrls.summary(memberId, statuses, false)
    );
  }

  getPerson(personId: number): Observable<PersonDto> {
    return this.api.get<PersonDto>(MemberUrls.person(personId));
  }

  getPersonAddresses(
    personId: number,
    type: string
  ): Observable<PaginatedResponseDto<AddressDto>> {
    return this.api.get<PaginatedResponseDto<AddressDto>>(
      MemberUrls.addresses(personId, type)
    );
  }

  updateMember(id: number, person: PersonModel): Observable<PersonDto> {
    return this.api.put<PersonModel>(MemberUrls.person(id), person);
  }

  importMember(
    memberNumber: string,
    branch: number,
    memberType: string
  ): Observable<MemberSyncDto> {
    const params = !!branch ? `?branchId=${branch}` : '';

    return this.api.put<MemberSyncDto>(
      `${MemberUrls.import(memberNumber, memberType)}${params}`
    );
  }

  importMemberById(memberId: number): Observable<MemberSyncDto> {
    return this.api.put<MemberSyncDto>(MemberUrls.importById(memberId));
  }

  checkImport(
    memberNumber: string,
    branch: number,
    memberType: string,
  ): Observable<MemberSyncDto> {
    const params = {
      branchId: branch,
    };
    return this.api.get<MemberSyncDto>(
      MemberUrls.import(memberNumber, memberType),
      params,
      null,
      true
    );
  }

  review(memberId: number): Observable<MemberProfileDto> {
    return this.api.put(MemberUrls.review(memberId));
  }

  getBranches(): Observable<PaginatedResponseDto<BranchDto>> {
    return this.api.get(MemberUrls.branches);
  }

  getOrganization(id: number): Observable<OrganizationModel> {
    return this.api.get(MemberUrls.organization(id));
  }

  getMemberAuthentication(memberId: number, deliveryType: string, memberType: string): Observable<any> {
    return this.api.post(MemberUrls.authenticate(memberId, deliveryType, memberType));
  }

  sendAuthenticationCode(payload: { memberId: string, deliveryType: string, recipientAddress: string }): Observable<any> {
    return this.api.post(MemberUrls.sendcode(), payload);
  }

  updateOrganization(
    id: number,
    org: OrganizationModel
  ): Observable<OrganizationDto> {
    return this.api.put<OrganizationModel>(MemberUrls.organization(id), org);
  }

  getLendings(accountId: number): Observable<LendingDto> {
    return this.api.get(MemberUrls.lendings(accountId));
  }

  getLendingHolds(
    accountId: number
  ): Observable<PaginatedResponseDto<HoldDto>> {
    return this.api.get(MemberUrls.lendingHolds(accountId));
  }

  getLendingCollateral(
    accountId: number
  ): Observable<PaginatedResponseDto<CollateralDto>> {
    return this.api.get(MemberUrls.lendingCollateral(accountId));
  }

  getSavings(accountId: number): Observable<SavingDto> {
    return this.api.get(MemberUrls.savings(accountId));
  }

  getSavingHolds(accountId: number): Observable<PaginatedResponseDto<HoldDto>> {
    return this.api.get(MemberUrls.savingHolds(accountId));
  }

  getNotes(accountId: number, isProspectInfo: boolean): Observable<PaginatedResponseDto<NoteDto>> {
    return this.api.get(NoteUrls.notes, { accountId, isProspectInfo, sort: 'desc' });
  }

  syncAccounts(memberId: number): Observable<any> {
    return this.api.put(MemberUrls.syncAccounts(memberId));
  }

  getAllLendings(
    memberId: number
  ): Observable<PaginatedResponseDto<LendingDto>> {
    return this.api.get(MemberUrls.allLendings(memberId));
  }

  getAllSavings(memberId: number): Observable<PaginatedResponseDto<SavingDto>> {
    return this.api.get(MemberUrls.allSavings(memberId));
  }

  getOrganizationPeople(
    memberId: number,
    request: PeopleApiRequest
  ): Observable<PaginatedResponseDto<PeopleDto>> {
    return this.api.get(MemberUrls.people(memberId), request);
  }

  updatePerson(
    jointId: number,
    model: PersonUpdateApiRequestModel
  ): Observable<PeopleDto> {
    return this.api.put<PeopleModel>(MemberUrls.joints(jointId), model);
  }

  addPersonToOrganization(
    primaryMemberId: number,
    request: AddPersonToOrganizationApiRequestModel
  ): Observable<PeopleDto> {
    return this.api.post<PeopleModel>(
      MemberUrls.people(primaryMemberId),
      request
    );
  }

  getProvinces(): Observable<PaginatedResponseDto<ProvinceModel>> {
    return this.api.get(MemberUrls.provinces);
  }

  updateAddress(
    address: AddressModel,
    personId: number,
    addressId: number,
    type: string
  ): Observable<AddressDto> {
    return this.api.put(MemberUrls.address(personId, type, addressId), address);
  }

  createAddress(
    address: AddressModel,
    type: string,
    id: number
  ): Observable<AddressDto> {
    return this.api.post(MemberUrls.createAddress(type, id), address);
  }

  checkAssociation(payload: { memberIds: number[], currentHouseholdId?: number }): Observable<number[]> {
    return this.api.post(MemberUrls.checkHouseholdAssociation, payload);
  }

  createHousehold(payload: { userId: number, memberIds: number[] }): Observable<number> {
    return this.api.post(MemberUrls.households, payload);
  }

  updateHousehold(id: number, payload: { userId: number, memberIds: number[] }): Observable<number> {
    return this.api.put(MemberUrls.household(id), payload);
  }

  getBankIntegrationConfiguration(): Observable<BankIntegrationConfigurationDto> {
    return this.api.get(MemberUrls.bankIntegrationConfiguration);
  }


  getCoreBankingIntegrationList(): Observable<CoreBankingIntegrationListDto> {
    return this.api.get(MemberUrls.coreBankingIntegrationList);
  }

  getOfferCount(
    memberId: number,
    statuses: string[]
  ): Observable<OffersSummaryDto> {
    return this.api.get<OffersSummaryDto>(
      MemberUrls.offerCount(memberId, statuses)
    );
  }

  getMemberProfileAccessHistory(request: AccessHistoryApiRequest): Observable<PaginatedResponseDto<AccessHistoryDto>> {
    return this.api.get<PaginatedResponseDto<AccessHistoryDto>>(MemberUrls.accessHistory(request.memberId), request);
  }
}
